import type {
  AgentSubType,
  BulkUpdateEntityType,
  CollectionSubType,
  Concept,
} from 'types';
import type {
  Data,
  LinkedLiterary,
  VerifiedLinkedAgentLink,
  VerifiedLinkedCatalogPost,
  VerifiedLinkedValueLink,
} from 'schemaDefinition/types';
import type {CodeListId} from 'services/codelists/types';
import type {
  ExpressionV4,
  ManifestationV4,
  OtherTitle,
  WorkMetadata,
  WorkV4,
} from 'services/metadata/types';
import type {TaskStatus} from 'services/tasks/types';
import type {HttpError} from './http/types';
import type {Schemas} from './dto.generated';

export type Tokens = {
  accessToken: string;
  idToken: string;
};

export type GetTokens = () => Promise<Tokens>;

export type LoadStatus = 'NotLoaded' | 'Loading' | 'Loaded' | 'Failed';

export type DataLoadStatus<TData> =
  | {
      status: 'NotLoaded' | 'Loading';
      partial?: boolean;
      data?: TData;
      error?: HttpError;
    }
  | {
      status: 'Loaded';
      partial?: boolean;
      data: TData;
      error?: HttpError;
    }
  | {
      status: 'Failed';
      partial?: boolean;
      data?: TData;
      error: HttpError;
    };

export type DataLoadByIdStatus<TData, TId extends string | number> = {
  [key in TId]?: DataLoadStatus<TData>;
};

export type UpdateStatus = {
  status: LoadStatus;
  error?: HttpError;
};

//=== Domain types =================================================================================
// I.e. domain types used in front-end, based on DTOs from the API.

//----- Task ---------------------------------------------------------------------------------------

export type ManifestationStatus =
  Required<Schemas.ManifestationBaseDto>['status'];

export type ExternalSuggestions = Schemas.ExternalSuggestionsDto;

/**
 * @deprecated Use ProductStatus instead
 */
export type AvailabilityStatus = Required<
  Required<Schemas.MetadataDto>
>['availabilityStatus'];

export type ProductStatus = Required<Required<Schemas.ProductDto>>['status'];

//----- Receive -----
export type RegisteredReceiveContentResponse =
  Schemas.PhysicalContentReceivedResponse;

//----- Data -----------------------------------------------------------------------------------

export type FilterType = Schemas.AvailableFilter['type'];
export type FilterOperation = Schemas.Filter['operation'] | 'BETWEEN';
export type FilterOperator = Schemas.Filter['operator'];

export type Filter = Omit<
  Schemas.Filter,
  'value' | 'operator' | 'operation'
> & {
  values?: Array<string>;
  operation: FilterOperation;
  generated?: boolean;
};

export type CurrentFilterType = Filter & {
  id: number;
  hidden?: boolean;
};

export type CustomFilterType = CurrentFilterType & {
  hidden: true;
};

export type GeneratedFilterType = CurrentFilterType & {
  generated: true;
};

export type FilterSet = Omit<Schemas.FilterSet, 'filters'> & {
  filters: Array<Filter | CustomFilterType>;
};

export type Column = Omit<Schemas.Column, 'codelistId'> & {
  codelistId?: Schemas.CodelistDto['id'];

  /**
   *  All columns are available on type manifestation, but only a few
   *  are available when viewmode is work.
   */
  workColumn?: boolean;
};
export type ColumnWithLabel = Column & {
  label: string;
};
export type ColumnSet = Schemas.ColumnSet;

export type Pageable = Required<Omit<Schemas.Pageable, 'sort'>> & {
  sort?: string;
};

export type ViewMode = BulkUpdateEntityType;
export type DataRow = Schemas.ExportRow;
export type DataResponse = {
  hits: number;
  rows: Data[];
};

export type AvailableFilter = Omit<
  Schemas.AvailableFilter,
  'codelistId' | 'operations'
> & {
  operations: Array<FilterOperation>;
  codelistId?: Schemas.CodelistDto['id'];
  generated?: boolean;
};

export type AvailableFilterWithLabel = AvailableFilter & {
  label: string;
  codelistId?: CodeListId;
};

//----- DateType -----------------------------------------------------------------------------------

export type DateType = Schemas.DateTypeDto & {
  bce?: boolean;
};

//----- AgentName ----------------------------------------------------------------------------------

export type AgentName = Omit<Schemas.AgentNameDto, 'nameOrder'> & {
  description?: string;
};

//----- Agent --------------------------------------------------------------------------------------

export type AgentTypeDto = Schemas.AgentDto['agentType'];

/**
 *  When not yet verified/resolved agent will have undefined id
 */
export type AgentBase = Omit<
  Schemas.AgentDto,
  'nameVariants' | 'agentType' | 'works' | 'prizes'
> & {
  nameVariants: AgentName[];
  works?: AgentWorkSummary[];
  prizes?: AgentPrizeWon[];
};

export type AgentPrizeWon = Omit<Schemas.AgentPrizeWon, 'prize' | 'work'> & {
  prize: {
    link: VerifiedLinkedValueLink;
  };
  work?: {
    link: VerifiedLinkedValueLink;
  };
};

export type AgentWorkSummary = WorkSummary &
  Pick<Schemas.AgentWorkRoleDto, 'roles'>;

export type AgentPerson = AgentBase &
  Omit<
    Schemas.AgentPersonDto,
    'agentType' | 'nameVariants' | 'works' | 'birth' | 'death' | 'prizes'
  > & {
    agentType: Extract<AgentSubType, Concept.person>;
    birth?: DateType;
    death?: DateType;
    links?: LinkedLiterary[];
  };

export type Gender = Schemas.AgentPersonDto['gender'];

export type AgentPublisher = AgentBase &
  Omit<
    Schemas.AgentPublisherDto,
    'agentType' | 'nameVariants' | 'works' | 'prizes'
  > & {
    agentType: Extract<AgentSubType, Concept.publisher>;
    links?: LinkedLiterary[];
  };

export type AgentNameWithDescription = AgentName & {description?: string};

/**
 * Adjust AgentCorporation to have description on each nameVariant
 * instead of agent object. Will be handled by mapping from/to
 * DTOs when doing API calls
 */
export type AgentCorporation = Omit<AgentBase, 'nameVariants'> &
  Omit<
    Schemas.AgentCorporationDto,
    'agentType' | 'nameVariants' | 'works' | 'description' | 'prizes'
  > & {
    agentType: Extract<AgentSubType, Concept.corporation>;
    nameVariants: AgentNameWithDescription[];
    links?: LinkedLiterary[];
    subsidiary?: string[];
  };

export type AgentEvent = Omit<AgentBase, 'agentType' | 'nameVariants'> & {
  agentType: Extract<AgentSubType, Concept.event>;
  place?: Schemas.AgentEventDto['place'];
  from?: Schemas.AgentEventDto['from'];
  to?: Schemas.AgentEventDto['to'];
  nameVariants: AgentNameWithDescription[];
  links?: LinkedLiterary[];
};

export type Agent =
  | AgentPerson
  | AgentPublisher
  | AgentCorporation
  | AgentEvent;

/**
 * Nasjonalt Autoritetsregister har ikke PUBLISHERs
 */
export type NationalAgentType = Exclude<AgentSubType, Concept.publisher>;

export type NationalRegistryStatus = 'kat1' | 'kat2' | 'kat3' | 'deleted';
/**
 * Nasjonalt Autoritetsregister har kun personer, korporasjoner og events
 */
export type NationalAgent = (AgentPerson | AgentCorporation | AgentEvent) & {
  registryStatus?: NationalRegistryStatus;
};

export type NorafUpdate = Omit<Schemas.NorafUpdate, 'agentType'> & {
  agentType: AgentSubType;
};

export type NorafUpdates = {
  total: number;
  pages: number;
  items: NorafUpdate[];
};

export type NorafUpdatesIgnoreListItem =
  Required<Schemas.NorafUpdatesIgnoreListItem>;

//----- Linked Value Links: Generalisation of link-part of LinkedAgent and LinkedLiterary  -----------

//----- LinkedAgent ----------------------------------------------------------------------------------

//----- LinkedLiterary -------------------------------------------------------------------------------

//----- Prize ----------------------------------------------------------------------------------------

export type PrizeWinner =
  | {
      type: 'NONE';
      id: string;
      year: number;
    }
  | (Required<Omit<Schemas.LiteraturePrizeWinner, 'agent' | 'work'>> & {
      agent: {
        link: VerifiedLinkedAgentLink;
      };
      work?: {
        link: VerifiedLinkedValueLink;
      };
    });

export type Prize = Required<
  Pick<Schemas.LiteraturePrizeWithWinners, 'id' | 'name' | 'presenter'>
> &
  Omit<
    Schemas.LiteraturePrizeWithWinners,
    'id' | 'name' | 'presenter' | 'winners' | 'month'
  > & {
    winners: PrizeWinner[];
    month?: string;
  };

//----- Media ----------------------------------------------------------------------------------------

export type MediaType = Schemas.MediaDto['mediaType'];
export type MediaSubType = Schemas.MediaDto['mediaSubType'];

export type Media = {
  id: Schemas.MediaDto['mediaId'];
  extension: Required<Schemas.MediaDto>['fileExtension'];
  type: Schemas.MediaDto['mediaType'];
  subType: Required<Schemas.MediaDto>['mediaSubType'];
  version: Required<Schemas.MediaDto>['version'];
  size?: Schemas.MediaDto['size'];
  compressed?: Schemas.MediaDto['compressed'];

  fileNr: number;
};

export type ManifestationsMedia = {
  manifestationId: string;
  medias: Media[];
};

//----- DownloadInfo -------------------------------------------------------------------------------

export type DownloadInfo = {
  uri: Required<Schemas.DownloadInfoDto>['uri'];
  status: Required<Schemas.DownloadInfoDto>['status'];
};

//----- DownloadFile -------------------------------------------------------------------------------

export type FileInfo = Schemas.FileInfoDto;
export type DownloadFile = Schemas.DownloadFileDto;

//----- Work ---------------------------------------------------------------------------------------

export type Dewey = Schemas.DeweyDto;

export type WorkPrizeWon = Required<
  Omit<Schemas.WorkPrizeWon, 'agent' | 'prize'>
> & {
  agent: {
    link: VerifiedLinkedAgentLink;
  };
  prize: {
    link: VerifiedLinkedValueLink;
  };
};

//----- WorkSummary --------------------------------------------------------------------------------

export type WorkSummary = WorkV4 & {
  // Using required to avoid undefined which causes type errors in typed schema
  mainTitles: Required<ManifestationV4>['mainTitles'] | null;
  themaLabels?: WorkV4['themes'];
  themaCodes?: WorkV4['themes'];
  otherTitleType36?: OtherTitle['value'][]; // Deltittel
  otherTitleType37?: OtherTitle['value'][]; // Tittelnummer
};

//----- Expression ---------------------------------------------------------------------------------

//----- Manifestation ------------------------------------------------------------------------------

//----- ChangeRequest ------------------------------------------------------------------------------

// TODO: Fix type
export type ChangeRequestType =
  | Extract<
      Schemas.TaskV2Dto['type'],
      'ML_MANUAL_APPROVAL' | 'METADATA_NEW' | 'METADATA_IMPORT_CHANGE'
    >
  | 'METADATA_MOVED'
  | 'METADATA_COPIED';

/**
 * backend: change request is stored in backend.
 *  Change request from ML, publisher or other external source.
 *
 * local: change request is stored in local storage.
 *  Stored locally during moving manifestation to another work.
 *  Stored values are from the work you moved from.
 *
 * transient: change request is not stored, only in memory.
 *  Used during copying from work/manifestation to current work/manifestation.
 */
export type ChangeRequestStorage = 'backend' | 'local' | 'transient';

/**
 * work: change request is on work level.
 *  Used when copying from one work to another.
 *
 * manifestation: change request is on manifestation level.
 *  Used when moving manifestation to another work.
 *  Plus all changerequests from backend (ML, publisher, etc.).
 */
export type ChangeRequestLevel = 'work' | 'manifestation';

export type ChangeRequest = {
  /** Mapped from taskId: number to id:string in API layer */
  id: string;
  taskType: ChangeRequestType;
  storage: ChangeRequestStorage;
  level: ChangeRequestLevel;
  work: WorkV4;
  expression: ExpressionV4;
  manifestation: ManifestationV4;
  externalSuggestions?: ExternalSuggestions;
  status?: TaskStatus | undefined;
};

export type ChangeRequestData = ChangeRequest[];

export type ChangeRequestApproval = Schemas.ManualApprovalChangeRequestDto;

//----- ML Prediction
export type MLPrediction = Schemas.MLPredictionDto;

//----- Series -------------------------------------------------------------------------------------
export type CollectionTypeDto = Schemas.SeriesWithRelationsDto['seriesType'];
export type CollectionTitleDto = Schemas.SeriesTitleDto;
export type CollectionTitle = CollectionTitleDto;

export type Collection = {
  id: string;
  collectionType: CollectionSubType;
  titles: CollectionTitle[];
  note?: string;
  links: VerifiedLinkedCatalogPost[];
};

export type RelationRoleDto =
  | Schemas.WorkToWorkRelationDto['role']
  | Schemas.AgentRelationDto['role']
  | Schemas.SeriesRelationDto['role']
  | Schemas.AgentCorporationRelationDto['role']
  | Schemas.AgentPersonRelationDto['role'];

export type RelationTargetTypeDto =
  | Schemas.WorkToWorkRelationDto['type']
  | Schemas.AgentRelationDto['type']
  | Schemas.SeriesRelationDto['type'];

//----- NoteData -----------------------------------------------------------------------------------

export type NoteContextType = 'manifestation';

export type NoteDataDto = Omit<Required<Schemas.CommentDto>, 'contextType'> & {
  contextType: NoteContextType;
};

export type NoteData = NoteDataDto & {
  /** client-side timestamp, updated on create and update */
  lastModified: number;
};

//===== Derived types ==============================================================================

export type WorkMetadataSuggestions = {
  work: Partial<WorkV4>;
  expressions: Array<Partial<ExpressionV4>>;
  manifestations: Array<Partial<ManifestationV4>>;
};

// ----- Search ----------------------------------------------------------------
/**
 * WorkHit is currently just an alias for WorkMetadata, but WorkHit (search response) might
 * be extended in the future. Also since WorkHit is sourced from elastic and not API, it should
 * not be used in any functional manner (e.g. linking), only presentations.
 */
export type WorkHit = WorkMetadata & {
  id: string;
};

//----- Mass Update --------------------------------------------------------------------------------
export type BulkUpdateCreate = {
  type: BulkUpdateEntityType;
  bulkUpdateField: BulkUpdateFieldName;
  bulkUpdateAction: BulkUpdateAction;
  /** Used by remove and replace */
  currentValues?: string[] | LinkedLiterary[];
  /** Used by add and replace */
  newValues?: string[] | LinkedLiterary[];
  addToFirstPosition?: boolean;
};

export type BulkUpdateCreateResponse = {
  id: string;
};

export type BulkUpdateAction = 'ADD' | 'REMOVE' | 'REPLACE';

export type BulkUpdateFieldName =
  | Exclude<Schemas.BulkUpdateWorkField['field'], 'seriesRelations'>
  | 'links';

export type BulkUpdateStatus =
  | 'NEW'
  | Schemas.BulkUpdateStatusResponse['status'];

export type BulkUpdateItemStatus =
  | 'NEW'
  | 'UNPROCESSED'
  | 'SUCCEEDED'
  | 'FAILED';

export type BulkUpdate = BulkUpdateCreate &
  Pick<Schemas.BulkUpdateStatusResponse, 'items'> & {
    id: string;
    status: BulkUpdateStatus;
    created: string; // Formatted as string in mapping
    completed?: string; // Formatted as string in mapping
  };

export type BulkUpdateList = {
  pages: number;
  elements: number;
  items: BulkUpdateSummary[];
};

export type BulkUpdateSummary = BulkUpdateCreate &
  Schemas.BulkUpdateSummaryItems &
  Pick<
    Schemas.BulkUpdateSummary,
    'id' | 'status' | 'items' | 'created' | 'completed'
  >;

export type BulkUpdateFailedItem = Schemas.BulkUpdateFailedItem;
export type BulkUpdateErrorKey =
  | 'WORK_ENTITY_NOT_FOUND'
  | 'VALUE_NOT_PRESENT'
  | 'VALUE_ALREADY_PRESENT'
  | 'INVALID_THEMA'
  | 'SYSTEM_ERROR';
export const BulkUpdateErrorKeys: BulkUpdateErrorKey[] = [
  'WORK_ENTITY_NOT_FOUND',
  'VALUE_NOT_PRESENT',
  'VALUE_ALREADY_PRESENT',
  'INVALID_THEMA',
  'SYSTEM_ERROR',
];
